/**
 * GuildSearchInput.jsx — Sprint-17 #157.
 *
 * Admin-only autosuggest that hits GET /api/v2/admin/guilds/search?q=.
 * Mirrors the ItemSearchInput pattern (250 ms debounce, clear button,
 * keyboard nav: ArrowUp/ArrowDown/Enter/Escape). Suggestion list is
 * rendered inline below the input and the parent receives the picked
 * guild via `onSelect({ id, name, index, isRedfur, salesCount })`.
 *
 * CSP-safe: all event wiring is via JSX handlers, no inline strings.
 */

import { createSignal, Show, For, onCleanup } from 'solid-js';

const DEFAULT_DEBOUNCE_MS = 250;

export default function GuildSearchInput(props) {
  const [raw, setRaw] = createSignal(props.initialValue || '');
  const [suggestions, setSuggestions] = createSignal([]);
  const [open, setOpen] = createSignal(false);
  const [highlight, setHighlight] = createSignal(-1);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal(null);
  let timer = 0;
  let abortCtl = null;

  function fetchSuggestions(q) {
    if (abortCtl) { try { abortCtl.abort(); } catch {} }
    abortCtl = new AbortController();
    setLoading(true);
    setError(null);
    fetch('/api/v2/admin/guilds/search?q=' + encodeURIComponent(q || ''), {
      credentials: 'include',
      signal: abortCtl.signal,
    })
      .then(r => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(j => {
        setSuggestions(Array.isArray(j?.guilds) ? j.guilds : []);
        setHighlight(-1);
        setLoading(false);
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Search failed');
        setSuggestions([]);
        setLoading(false);
      });
  }

  function schedule(v) {
    clearTimeout(timer);
    const ms = Number.isFinite(props.debounceMs) ? props.debounceMs : DEFAULT_DEBOUNCE_MS;
    timer = setTimeout(() => fetchSuggestions(v), ms);
  }
  onCleanup(() => {
    clearTimeout(timer);
    if (abortCtl) { try { abortCtl.abort(); } catch {} }
  });

  function handleInput(e) {
    const v = e.currentTarget.value;
    setRaw(v);
    setOpen(true);
    schedule(v);
  }

  function handleFocus() {
    setOpen(true);
    if (suggestions().length === 0 && !loading()) schedule(raw());
  }

  function handleBlur() {
    // Delay so a click on a suggestion item still registers.
    setTimeout(() => setOpen(false), 150);
  }

  function pick(g) {
    if (!g) return;
    setRaw(g.name);
    setOpen(false);
    setHighlight(-1);
    if (typeof props.onSelect === 'function') props.onSelect(g);
  }

  function clear() {
    clearTimeout(timer);
    setRaw('');
    setSuggestions([]);
    setHighlight(-1);
    setOpen(false);
    if (typeof props.onClear === 'function') props.onClear();
  }

  function handleKey(e) {
    const list = suggestions();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight(h => Math.min((h < 0 ? -1 : h) + 1, list.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, -1));
    } else if (e.key === 'Enter') {
      const idx = highlight();
      if (idx >= 0 && list[idx]) {
        e.preventDefault();
        pick(list[idx]);
      } else if (list.length === 1) {
        e.preventDefault();
        pick(list[0]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
  }

  return (
    <div class={`v2-guild-search ${props.class || ''}`}>
      <div class="v2-item-search">
        <input
          class={`v2-input v2-item-search__input ${props.inputClass || ''}`}
          type="search"
          placeholder={props.placeholder || 'Search guilds…'}
          value={raw()}
          onInput={handleInput}
          onKeyDown={handleKey}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autocomplete="off"
          spellcheck={false}
          role="combobox"
          aria-expanded={open()}
          aria-autocomplete="list"
        />
        <Show when={raw()}>
          <button
            class="v2-btn-sm v2-item-search__clear"
            onClick={clear}
            title="Clear"
            type="button">✖</button>
        </Show>
      </div>
      <Show when={open() && (loading() || error() || suggestions().length > 0)}>
        <ul class="v2-guild-search__list" role="listbox">
          <Show when={loading()}>
            <li class="v2-guild-search__hint">Searching…</li>
          </Show>
          <Show when={error()}>
            <li class="v2-guild-search__hint v2-guild-search__hint--err">{error()}</li>
          </Show>
          <For each={suggestions()}>{(g, i) => (
            <li
              class={`v2-guild-search__item ${highlight() === i() ? 'is-active' : ''} ${g.isRedfur ? 'is-redfur' : ''}`}
              role="option"
              aria-selected={highlight() === i()}
              onMouseDown={(e) => { e.preventDefault(); pick(g); }}
              onMouseEnter={() => setHighlight(i())}
            >
              <span class="v2-guild-search__name">{g.name}</span>
              <Show when={g.isRedfur}>
                <span class="v2-guild-search__badge" title="Redfur tracked guild">RF</span>
              </Show>
              <span class="v2-guild-search__count" title={`${g.salesCount} sales on record`}>
                {g.salesCount > 0 ? g.salesCount.toLocaleString() : '—'}
              </span>
            </li>
          )}</For>
          <Show when={!loading() && !error() && suggestions().length === 0}>
            <li class="v2-guild-search__hint">No guilds match.</li>
          </Show>
        </ul>
      </Show>
    </div>
  );
}
